import { usePwaInstall, useUserClient } from '@cascade/react-dom/hooks';
import { company } from '@cascade/core/constants';
import { useI18n } from '@cascade/react/i18n';
import { trackEvent } from '@cascade/react-dom/observability';
import { readStorageItem, writeStorageItem } from '@cascade/react-dom/utils';
import { Button, Image, Text } from '@cascade/ui-library';
import { useEffect, useState } from 'react';
import { InstallGuide } from './install-guide/index.ts';
import classes from './InstallBanner.module.scss';

const DISMISSED_AT_KEY = 'cascade.install.dismissedAt';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

const readIsSnoozed = (): boolean => {
  const dismissedAt = readStorageItem(DISMISSED_AT_KEY);

  return Date.now() - Number(dismissedAt) < SNOOZE_MS;
};

const trackInstall = () => {
  trackEvent('pwa_installed');
};

export const InstallBanner = () => {
  const { translate } = useI18n();
  const { isMobile } = useUserClient();
  const { platform, isStandalone, promptInstall } = usePwaInstall({
    isEnabled: isMobile,
    onInstalled: trackInstall,
  });

  const [isSnoozed, setIsSnoozed] = useState(readIsSnoozed);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const title = translate('install.title');
  const isShown =
    isMobile && platform !== 'unsupported' && !isStandalone && !isSnoozed;

  const dismiss = () => {
    const dismissedAt = String(Date.now());

    writeStorageItem(DISMISSED_AT_KEY, dismissedAt);
    setIsSnoozed(true);
    trackEvent('pwa_banner_dismissed');
  };

  const closeGuide = () => {
    setIsGuideOpen(false);
    dismiss();
  };

  const install = () => {
    if (platform === 'ios') {
      trackEvent('pwa_install_ios_help');
      setIsGuideOpen(true);

      return;
    }

    trackEvent('pwa_install_prompted');
    promptInstall();
  };

  useEffect(() => {
    if (!isShown) {
      return;
    }

    trackEvent('pwa_banner_shown', { platform });
  }, [isShown, platform]);

  if (!isShown) {
    return null;
  }

  if (isGuideOpen) {
    return <InstallGuide onClose={closeGuide} />;
  }

  return (
    <section aria-label={title} className={classes.banner}>
      <Image
        src='/icons/icon-192.png'
        title={company.name}
        className={classes.icon}
      />
      <div className={classes.copy}>
        <Text content={title} weight='semibold' />
        <Text
          type='body-2'
          color='text-60'
          content={translate('install.description')}
        />
      </div>
      <div className={classes.actions}>
        <Button
          variant='primary'
          size='sm'
          iconType='download'
          label={translate('install.action')}
          onClick={install}
        />
        <Button
          variant='secondary'
          size='sm'
          iconType='close'
          label={translate('install.dismiss')}
          onClick={dismiss}
        />
      </div>
    </section>
  );
};
